import type { Item } from '@/types/prioritization/lifecycle';

export const ITEM_PRINT_FIELD_IDS = [
  'title',
  'description',
  'id',
  'tags',
  'source',
  'item_category',
  'meta_category',
  'strategic_bucket',
  'altitude_level',
  'work_type',
  'exit_status',
  'lifecycle_stage',
  'planning_maturity',
  'knowledge_status',
  'confidence_level',
  'desired_outcome',
  'beneficiary',
  'queue_hint',
  'created_at',
  'updated_at',
  'created_by',
  'owner_id',
] as const;

export type ItemPrintFieldId = (typeof ITEM_PRINT_FIELD_IDS)[number];

export type ItemPrintFieldVisibility = Record<ItemPrintFieldId, boolean>;

export const ITEM_PRINT_FIELD_LABELS: Record<ItemPrintFieldId, string> = {
  title: 'Title',
  description: 'Description',
  id: 'Internal ID',
  tags: 'Tags',
  source: 'Source',
  item_category: 'Item category',
  meta_category: 'Meta category',
  strategic_bucket: 'Strategic bucket',
  altitude_level: 'Altitude level',
  work_type: 'Work type',
  exit_status: 'Exit status',
  lifecycle_stage: 'Lifecycle stage (legacy)',
  planning_maturity: 'Planning maturity',
  knowledge_status: 'Knowledge status',
  confidence_level: 'Confidence level',
  desired_outcome: 'Desired outcome',
  beneficiary: 'Beneficiary',
  queue_hint: 'Queue / routing',
  created_at: 'Created',
  updated_at: 'Updated',
  created_by: 'Recorded creator',
  owner_id: 'Owner ID',
};

const STORAGE_KEY_FIELDS = 'priorities-ai:item-print-fields-v1';

function defaultVisibility(): ItemPrintFieldVisibility {
  const v = {} as ItemPrintFieldVisibility;
  for (const id of ITEM_PRINT_FIELD_IDS) {
    v[id] = id !== 'id' && id !== 'owner_id' && id !== 'queue_hint';
  }
  return v;
}

export function loadItemPrintVisibility(): ItemPrintFieldVisibility {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FIELDS);
    if (!raw) return defaultVisibility();
    const parsed = JSON.parse(raw) as Partial<ItemPrintFieldVisibility>;
    const base = defaultVisibility();
    for (const id of ITEM_PRINT_FIELD_IDS) {
      if (typeof parsed[id] === 'boolean') base[id] = parsed[id]!;
    }
    return base;
  } catch {
    return defaultVisibility();
  }
}

export function saveItemPrintVisibility(next: ItemPrintFieldVisibility): void {
  try {
    localStorage.setItem(STORAGE_KEY_FIELDS, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function fmtDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toLocaleString();
  return '';
}

/** Format helpers for neutral print typography (caller supplies labels). */
export function formatItemFieldValue(
  field: ItemPrintFieldId,
  item: Item,
  opts?: { queueLabel?: string | null },
): string {
  switch (field) {
    case 'title':
      return item.title ?? '';
    case 'description':
      return item.description ?? '';
    case 'id':
      return item.id;
    case 'tags':
      return (item.tags ?? []).join(', ');
    case 'source':
      return item.source ?? '';
    case 'item_category':
      return item.itemCategory ?? '';
    case 'meta_category':
      return item.metaCategory ?? '';
    case 'strategic_bucket':
      return item.strategicBucket ?? '';
    case 'altitude_level':
      return item.altitudeLevel != null ? String(item.altitudeLevel) : '';
    case 'work_type':
      return item.work_type ?? '';
    case 'exit_status':
      return item.exit_status ?? '';
    case 'lifecycle_stage':
      return item.lifecycle_stage ?? '';
    case 'planning_maturity':
      return item.planning_maturity ?? '';
    case 'knowledge_status':
      return item.knowledge_status ?? '';
    case 'confidence_level':
      return item.confidence_level ?? '';
    case 'desired_outcome':
      return item.desiredOutcome ?? '';
    case 'beneficiary':
      return item.beneficiary ?? '';
    case 'queue_hint': {
      const id = item.queueId ?? '';
      const label = opts?.queueLabel?.trim();
      if (label) return id ? `${label} (${id})` : label;
      return id;
    }
    case 'created_at':
      return fmtDate(item.createdAt);
    case 'updated_at':
      return fmtDate(item.updatedAt);
    case 'created_by':
      return item.createdBy ?? '';
    case 'owner_id':
      return item.ownerId ?? item.shapingOwnerId ?? '';
    default:
      return '';
  }
}

export function fieldHasRenderableValue(
  field: ItemPrintFieldId,
  item: Item,
  opts?: { queueLabel?: string | null },
): boolean {
  const s = formatItemFieldValue(field, item, opts).trim();
  return s.length > 0;
}
